import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
} from "@/ui-kit";
import classNames from "classnames";
import { CalendlyPopUp } from "components/ContentfulRenderer/ContentfulComponent/Calendly/PopUp";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useWebTranslation } from "translations";
import { Button } from "ui-kit/Button";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { getMeetingBookingUrl } from "utils/site";
import { LanguageSelector } from "../../LanguageSelector/LanguageSelector";
import { Hamburger } from "./Hamburger";
import styles from "./Mobile.module.css";
import { MenuBarProps, MenuGroup, MenuItem } from "./types";

const TopLevelLink = ({ link }: { link: MenuItem }) => {
  return (
    <Link href={link.uri ?? ""}>
      <Typography
        variant="body2"
        className={styles.topLevelLink}
        disableRedesignColor
      >
        <strong>{link.name}</strong>
      </Typography>
    </Link>
  );
};

const SubLevelLink = ({ link }: { link: MenuItem }) => {
  return (
    <Link href={link.uri ?? ""}>
      <Typography
        variant="body3"
        className={styles.menuLink}
        disableRedesignColor
      >
        <strong>{link.name}</strong>
      </Typography>
    </Link>
  );
};

const MobileMenu = ({ item }: { item: MenuGroup }) => {
  return (
    <Accordion collapsible>
      <AccordionItem>
        <AccordionTitle>{item.group}</AccordionTitle>
        <AccordionPanel>
          <>
            {item.items.map((menuitem) => {
              return "subMenu" in menuitem ? (
                <div key={menuitem.headline.name}>
                  <Typography
                    variant="body4"
                    className={styles.categoryHeader}
                    disableRedesignColor
                  >
                    <strong>{menuitem.headline.name}</strong>
                  </Typography>
                  {menuitem.subMenu?.map((subMenuItem) => {
                    return (
                      <SubLevelLink link={subMenuItem} key={subMenuItem.name} />
                    );
                  })}
                </div>
              ) : (
                <Link
                  href={menuitem.headline.uri || ""}
                  key={menuitem.headline.name}
                >
                  <Typography
                    variant="body3"
                    className={styles.menuLink}
                    disableRedesignColor
                  >
                    <strong>{menuitem.headline.name}</strong>
                  </Typography>
                </Link>
              );
            })}
          </>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export const MenuBarMobile = ({
  navigationItems,
  siteLanguages,
  onMenuToggle,
}: MenuBarProps) => {
  const router = useRouter();
  const { t } = useWebTranslation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const panelId = useId();
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // The header hides its banner while the menu is open, so every way of
  // closing the menu tells it.
  const closeMenu = useCallback(() => {
    onMenuToggle && onMenuToggle(false);
    setMobileMenuOpen(false);
  }, [onMenuToggle]);

  // Escape closes only the innermost open thing: while the language picker's
  // list is open, its button still reads as expanded here, and its own handler
  // closes the list.
  const closeOnEscape = (event: KeyboardEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;

    if (
      event.key !== "Escape" ||
      !mobileMenuOpen ||
      (target !== hamburgerRef.current &&
        target.getAttribute("aria-expanded") === "true")
    ) {
      return;
    }

    closeMenu();
    hamburgerRef.current?.focus();
  };

  useEffect(() => {
    router.events.on("routeChangeStart", closeMenu);

    return () => router.events.off("routeChangeStart", closeMenu);
  }, [router.events, closeMenu]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const downloadAppUrl = t("navigation.download_app_cta.url");

  const meetingBookingUrl = getMeetingBookingUrl();

  return (
    <>
      <Hamburger
        ref={hamburgerRef}
        onClick={() => {
          onMenuToggle && onMenuToggle(!mobileMenuOpen);
          setMobileMenuOpen(!mobileMenuOpen);
        }}
        onKeyDown={closeOnEscape}
        isOpen={mobileMenuOpen}
        controls={panelId}
      />
      <div
        className={classNames(styles.backdrop, mobileMenuOpen && styles.active)}
        onClick={closeMenu}
      />

      <div
        id={panelId}
        className={classNames(
          styles.mobilePanel,
          mobileMenuOpen && styles.active
        )}
        onKeyDown={closeOnEscape}
      >
        <nav className={styles.wrapper}>
          <div className={styles.navItems}>
            {navigationItems.map((item) => {
              return "group" in item ? (
                <MobileMenu item={item} key={item.group} />
              ) : (
                <TopLevelLink link={item} key={item.name} />
              );
            })}

            {downloadAppUrl.length > 0 ? (
              <TopLevelLink
                link={{
                  uri: downloadAppUrl,
                  name: t("navigation.download_app_cta.label"),
                }}
              />
            ) : null}
          </div>

          <div className={styles.bottomSectionLinks}>
            <LanguageSelector />
            {meetingBookingUrl ? (
              <>
                <Gutter size={2} />
                <CalendlyPopUp
                  meetingBookingUrl={meetingBookingUrl}
                  buttonStyles={styles.button}
                  buttonTitle={t("c2a.schedule_call")}
                />
              </>
            ) : null}
            <Button
              title={t("home.sections.0.btn")}
              onPress={() => {
                router.push("/signup");
              }}
              className={styles.signupButton}
            />
          </div>
        </nav>
      </div>
    </>
  );
};
